import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Activity, CheckCircle2, Clock, AlertCircle, RefreshCw, Sparkles, Server } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    AllServicesHealth,
    ServiceHealthCheck,
    ServiceStatus,
    ServiceName,
    HistoryEntry,
    DayHistory,
} from "@/types/health";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AIDashboard from "@/components/dashboard/AIDashboard";

const MAX_HISTORY_POINTS = 90;

const getStatusConfig = (t: any): Record<
    ServiceStatus,
    {
        label: string;
        badgeVariant: "default" | "secondary" | "destructive" | "outline";
        icon: React.ComponentType<{ className?: string }>;
        iconColor: string;
        dotColor: string;
    }
> => ({
    operational: {
        label: t("dashboard.operational"),
        badgeVariant: "outline",
        icon: CheckCircle2,
        iconColor: "text-green-600 dark:text-green-400",
        dotColor: "bg-green-600",
    },
    delayed: {
        label: t("dashboard.delayed"),
        badgeVariant: "outline",
        icon: Clock,
        iconColor: "text-yellow-600 dark:text-yellow-400",
        dotColor: "bg-yellow-600",
    },
    down: {
        label: t("dashboard.down"),
        badgeVariant: "outline",
        icon: AlertCircle,
        iconColor: "text-red-600 dark:text-red-400",
        dotColor: "bg-red-600",
    },
});

const getServiceDisplayNames = (t: any): Record<string, string> => ({
    backend: t("dashboard.services.backend"),
    web: t("dashboard.services.web"),
    mastra: t("dashboard.services.mastra"),
    qdrant: t("dashboard.services.qdrant"),
    postgres: t("dashboard.services.postgres"),
    redis: t("dashboard.services.redis"),
});

const ServiceCard = ({ service, t }: { service: ServiceHealthCheck | undefined; t: any }) => {
    // If service is undefined, show a skeleton or error state
    if (!service) {
        return <ServiceCardSkeleton />;
    }

    const statusConfig = getStatusConfig(t);
    const serviceDisplayNames = getServiceDisplayNames(t);
    const config = statusConfig[service.status];
    const Icon = config.icon;

    return (
        <Card className="transition-shadow duration-200 hover:shadow-md">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className={`h-2 w-2 rounded-full ${config.dotColor} animate-pulse`}></div>
                        <CardTitle className="text-base font-medium">
                            {serviceDisplayNames[service.service] || service.service}
                        </CardTitle>
                    </div>
                    <Icon className={`h-5 w-5 ${config.iconColor}`} />
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t("dashboard.status")}</span>
                    <Badge variant={config.badgeVariant} className="text-xs">
                        {config.label}
                    </Badge>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t("dashboard.current")}</span>
                    <span className="font-mono text-sm font-medium">{service.responseTime}ms</span>
                </div>
                {service.avgResponseTime !== undefined && (
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">{t("dashboard.average24h")}</span>
                        <span className="font-mono text-sm font-medium text-muted-foreground">
                            {Math.round(service.avgResponseTime)}ms
                        </span>
                    </div>
                )}
                {service.message && (
                    <div className="border-t pt-2">
                        <p className="text-xs text-muted-foreground">{service.message}</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

const ServiceCardSkeleton = () => (
    <Card>
        <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-5 w-20" />
            </div>
            <Skeleton className="mt-2 h-4 w-48" />
        </CardHeader>
        <CardContent>
            <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-16" />
            </div>
        </CardContent>
    </Card>
);

function SystemDashboard() {
    const { t } = useTranslation();
    const [health, setHealth] = useState<AllServicesHealth | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dayHistory, setDayHistory] = useState<DayHistory[]>([]);
    const [historyLoading, setHistoryLoading] = useState(true);
    
    const statusConfig = getStatusConfig(t);
    const serviceDisplayNames = getServiceDisplayNames(t);

    const fetchHistoricalData = async () => {
        try {
            const response = await fetch("/health/history?days=90", {
                credentials: "include",
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch history: ${response.status}`);
            }

            const result = await response.json();
            console.log("Fetched health history", result);

            // Transform the data into DayHistory format
            // Map: date -> { status, serviceData: Map<service_name, { statuses, count }>, totalChecks }
            const historyMap = new Map<
                string,
                {
                    date: string;
                    status: ServiceStatus;
                    serviceData: Map<string, { status: ServiceStatus; count: number }>;
                    totalChecks: number;
                }
            >();

            for (const row of result.data) {
                const date = new Date(row.date).toISOString().split("T")[0];

                if (!historyMap.has(date)) {
                    historyMap.set(date, {
                        date,
                        status: "operational",
                        serviceData: new Map(),
                        totalChecks: 0,
                    });
                }

                const dayEntry = historyMap.get(date)!;

                // Store service-level data
                const serviceName = row.service_name as ServiceName;
                const serviceStatus = row.status as ServiceStatus;
                // DuckDB returns numbers directly, not strings
                const checkCount =
                    typeof row.check_count === "number"
                        ? row.check_count
                        : typeof row.check_count === "string"
                          ? parseInt(row.check_count)
                          : 0;

                // Add to total check count for this day
                dayEntry.totalChecks += checkCount;

                // Keep track of worst status per service
                if (!dayEntry.serviceData.has(serviceName)) {
                    dayEntry.serviceData.set(serviceName, {
                        status: serviceStatus,
                        count: checkCount,
                    });
                } else {
                    const existing = dayEntry.serviceData.get(serviceName)!;
                    // Update to worse status if found
                    if (serviceStatus === "down") {
                        existing.status = "down";
                    } else if (serviceStatus === "delayed" && existing.status !== "down") {
                        existing.status = "delayed";
                    }
                    existing.count += checkCount;
                }

                // Update day's overall status to worst status seen
                if (serviceStatus === "down") {
                    dayEntry.status = "down";
                } else if (serviceStatus === "delayed" && dayEntry.status !== "down") {
                    dayEntry.status = "delayed";
                }
            }

            // Convert map to sorted array (oldest to newest) with proper checks structure
            const historyArray: DayHistory[] = Array.from(historyMap.values())
                .map((day) => {
                    // Create a single check entry representing the day's aggregate
                    const services: Record<ServiceName, ServiceStatus> = {
                        backend: "operational",
                        web: "operational",
                        mastra: "operational",
                        qdrant: "operational",
                        postgres: "operational",
                        redis: "operational",
                    };

                    // Fill in actual statuses from DuckDB data
                    day.serviceData.forEach((data, serviceName) => {
                        services[serviceName as ServiceName] = data.status;
                    });

                    // Create checks array with one entry per actual check (up to totalChecks)
                    // For tooltip purposes, we'll create a single representative check with metadata
                    const checks: HistoryEntry[] = [
                        {
                            overallStatus: day.status,
                            timestamp: new Date(day.date),
                            services,
                        },
                    ];

                    // Store total count in the first check for tooltip access
                    checks[0].totalCheckCount = day.totalChecks;

                    return {
                        date: day.date,
                        status: day.status,
                        checks,
                    };
                })
                .sort((a, b) => a.date.localeCompare(b.date));

            setDayHistory(historyArray);
            setHistoryLoading(false);
        } catch (err) {
            console.error("Failed to fetch historical data:", err);
            setHistoryLoading(false);
        }
    };

    const addToHistory = (newHealth: AllServicesHealth) => {
        // Calculate overall status from all services
        const services = Object.values(newHealth);
        let overallStatus: ServiceStatus = "operational";

        if (services.some((s) => s.status === "down")) {
            overallStatus = "down";
        } else if (services.some((s) => s.status === "delayed")) {
            overallStatus = "delayed";
        }

        const entry: HistoryEntry = {
            overallStatus,
            timestamp: new Date(),
            services: {
                backend: newHealth.backend.status,
                web: newHealth.web.status,
                mastra: newHealth.mastra.status,
                qdrant: newHealth.qdrant.status,
                postgres: newHealth.postgres.status,
                redis: newHealth.redis.status,
            },
        };

        // Get today's date in YYYY-MM-DD format
        const today = new Date().toISOString().split("T")[0];

        setDayHistory((prev) => {
            const updated = [...prev];

            // Find if we already have an entry for today
            const todayIndex = updated.findIndex((day) => day.date === today);

            if (todayIndex >= 0) {
                // Add check to existing day
                const todayEntry = updated[todayIndex];
                todayEntry.checks.push(entry);

                // Update day's overall status to worst status
                const worstStatus = todayEntry.checks.reduce((worst, check) => {
                    if (check.overallStatus === "down" || worst === "down") return "down";
                    if (check.overallStatus === "delayed" || worst === "delayed") return "delayed";
                    return "operational";
                }, "operational" as ServiceStatus);

                todayEntry.status = worstStatus;
            } else {
                // Create new day entry
                updated.push({
                    date: today,
                    status: overallStatus,
                    checks: [entry],
                });
            }

            // Keep only the last MAX_HISTORY_POINTS days
            if (updated.length > MAX_HISTORY_POINTS) {
                updated.shift();
            }

            return updated;
        });
    };

    const fetchHealthStatus = async (isRefresh = false) => {
        if (isRefresh) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }
        setError(null);

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);

            const response = await fetch("/health/services", {
                signal: controller.signal,
                credentials: "include",
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const text = await response.text();
                if (text.includes("Cannot GET")) {
                    throw new Error(
                        "Health endpoint not found. Please restart the backend server to load the new endpoint."
                    );
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: AllServicesHealth = await response.json();
            setHealth(data);
            addToHistory(data);
        } catch (err) {
            let errorMessage = "Failed to fetch health status";

            if (err instanceof Error) {
                if (err.name === "AbortError") {
                    errorMessage = "Request timed out after 10 seconds";
                } else if (err.message.includes("JSON.parse")) {
                    errorMessage =
                        "Backend returned invalid response. The /health/services endpoint may not be available yet. Please restart the backend.";
                } else {
                    errorMessage = err.message;
                }
            }

            setError(errorMessage);
            toast.error(t("dashboard.errorLoading"), {
                description: errorMessage,
            });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        // Initial fetch
        fetchHealthStatus();
        fetchHistoricalData();

        // Auto-refresh health status every 30 seconds
        const interval = setInterval(() => {
            fetchHealthStatus(true);
        }, 30000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRefresh = () => {
        fetchHealthStatus(true);
    };

    const getOverallStatus = (): ServiceStatus => {
        if (!health) return "down";

        const services = Object.values(health);
        if (services.some((s) => s.status === "down")) return "down";
        if (services.some((s) => s.status === "delayed")) return "delayed";
        return "operational";
    };

    const overallStatus = getOverallStatus();
    const overallConfig = statusConfig[overallStatus];
    const OverallIcon = overallConfig.icon;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold">{t("dashboard.systemHealth")}</h2>
                    <p className="text-sm text-muted-foreground">{t("dashboard.realtimeMonitoring")}</p>
                </div>
                <Button
                    onClick={handleRefresh}
                    disabled={loading || refreshing}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
                    {t("dashboard.refresh")}
                </Button>
            </div>

            {/* Overall Status Card */}
            {!loading && health && (
                <Card className="border-2">
                    <CardHeader>
                        <div className="flex items-center gap-4">
                            <div className={`rounded-lg p-2 ${overallConfig.dotColor} bg-opacity-10`}>
                                <OverallIcon className={`h-6 w-6 ${overallConfig.iconColor}`} />
                            </div>
                            <div className="flex-1">
                                <CardTitle className="text-xl font-semibold">{t("dashboard.systemStatus")}</CardTitle>
                                <CardDescription className="text-sm">
                                    {t("dashboard.allServicesMonitored")}
                                </CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className={`h-2 w-2 rounded-full ${overallConfig.dotColor} animate-pulse`}></div>
                                <span className="text-sm font-medium">{overallConfig.label}</span>
                            </div>
                        </div>
                    </CardHeader>
                </Card>
            )}

            {/* Error State */}
            {error && !loading && (
                <Card className="border-red-500/20 bg-red-500/10">
                    <CardHeader>
                        <div className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-red-500" />
                            <CardTitle className="text-red-500">{t("dashboard.errorLoading")}</CardTitle>
                        </div>
                        <CardDescription>{error}</CardDescription>
                    </CardHeader>
                </Card>
            )}

            {/* Service Cards Grid */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {loading ? (
                    <>
                        {[...Array(6)].map((_, i) => (
                            <ServiceCardSkeleton key={i} />
                        ))}
                    </>
                ) : health ? (
                    <>
                        <ServiceCard service={health.backend} t={t} />
                        <ServiceCard service={health.web} t={t} />
                        <ServiceCard service={health.mastra} t={t} />
                        <ServiceCard service={health.qdrant} t={t} />
                        <ServiceCard service={health.postgres} t={t} />
                        <ServiceCard service={health.redis} t={t} />
                    </>
                ) : null}
            </div>

            {/* Auto-refresh indicator */}
            {!loading && health && (
                <div className="text-center text-sm text-muted-foreground">{t("dashboard.autoRefresh")}</div>
            )}

            {/* Overall System Uptime */}
            <div className="mt-8 border-t pt-8">
                <div className="mb-4 flex items-start gap-3">
                    <div className="rounded bg-gray-100 p-2 dark:bg-gray-800">
                        <Activity className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">{t("dashboard.allServices")}</h3>
                        <p className="mt-1 text-xs text-muted-foreground">
                            {historyLoading ? t("dashboard.loadingHistory") : t("dashboard.lastDays", { count: dayHistory.length })}
                        </p>
                    </div>
                    <span className="text-sm font-medium text-green-600">
                        {dayHistory.length > 0 && dayHistory[dayHistory.length - 1].status === "operational"
                            ? t("dashboard.operational")
                            : dayHistory.length > 0 && dayHistory[dayHistory.length - 1].status === "delayed"
                              ? t("dashboard.delayed")
                              : dayHistory.length > 0
                                ? t("dashboard.down")
                                : historyLoading
                                  ? t("common.loading")
                                  : t("dashboard.operational")}
                    </span>
                </div>

                {historyLoading ? (
                    <div className="mb-2 flex h-12 items-center justify-center">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <RefreshCw className="h-4 w-4 animate-spin" />
                            {t("dashboard.loading90Day")}
                        </div>
                    </div>
                ) : (
                    <div className="mb-2 flex h-12 items-end gap-0.5">
                        {Array.from({ length: MAX_HISTORY_POINTS }).map((_, index) => {
                            // Calculate the actual index in dayHistory
                            // We want to show from oldest to newest (left to right)
                            // But fill from right to left initially
                            const historyIndex = dayHistory.length - MAX_HISTORY_POINTS + index;
                            const day = historyIndex >= 0 ? dayHistory[historyIndex] : null;
                            const hasData = historyIndex >= 0 && day !== null;

                            // Build tooltip content
                            let tooltipContent = "";
                            if (hasData && day) {
                                const date = new Date(day.date).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                });

                                // Get total check count
                                const totalChecks = day.checks[0]?.totalCheckCount || 0;

                                if (day.status === "operational") {
                                    tooltipContent = `${date}\nAll services operational (${totalChecks} checks)`;
                                } else {
                                    // Find which services had issues
                                    const problemServices: string[] = [];
                                    day.checks.forEach((check) => {
                                        (Object.entries(check.services) as [ServiceName, ServiceStatus][])
                                            .filter(([, status]) => status !== "operational")
                                            .forEach(([name, status]) => {
                                                const displayName = serviceDisplayNames[name] || name;
                                                problemServices.push(`${displayName}: ${status}`);
                                            });
                                    });

                                    if (problemServices.length > 0) {
                                        tooltipContent = `${date}\n\nIssues:\n${problemServices.join("\n")}\n\n(${totalChecks} checks)`;
                                    } else {
                                        tooltipContent = `${date}\nAll services operational (${totalChecks} checks)`;
                                    }
                                }
                            } else {
                                tooltipContent = "No data yet";
                            }

                            return (
                                <div
                                    key={index}
                                    className={`flex-1 cursor-pointer rounded-sm transition-all hover:opacity-80 ${
                                        !hasData
                                            ? "bg-gray-200 dark:bg-gray-700"
                                            : day.status === "operational"
                                              ? "bg-green-500"
                                              : day.status === "delayed"
                                                ? "bg-yellow-500"
                                                : "bg-red-500"
                                    }`}
                                    style={{
                                        height: `${100}%`,
                                    }}
                                    title={tooltipContent}
                                />
                            );
                        })}
                    </div>
                )}

                {!historyLoading && (
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{t("dashboard.daysAgo")}</span>
                        <span>
                            {dayHistory.length > 0
                                ? t("dashboard.uptime", { percent: Math.round((dayHistory.filter((d) => d.status === "operational").length / dayHistory.length) * 100) })
                                : t("dashboard.uptime", { percent: 100 })}
                        </span>
                        <span>{t("dashboard.today")}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function Dashboard() {
    const { t } = useTranslation();

    return (
        <div className="container mx-auto max-w-7xl space-y-6 p-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">{t("dashboard.title")}</h1>
                <p className="mt-1 text-sm text-muted-foreground">{t("dashboard.subtitle")}</p>
            </div>

            {/* Dashboard Tabs */}
            <Tabs defaultValue="ai" className="w-full">
                <TabsList className="grid w-full max-w-md grid-cols-2">
                    <TabsTrigger value="ai" className="gap-2">
                        <Sparkles className="h-4 w-4" />
                        {t("dashboard.tabs.ai")}
                    </TabsTrigger>
                    <TabsTrigger value="system" className="gap-2">
                        <Server className="h-4 w-4" />
                        {t("dashboard.tabs.system")}
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="ai" className="mt-6">
                    <AIDashboard />
                </TabsContent>
                <TabsContent value="system" className="mt-6">
                    <SystemDashboard />
                </TabsContent>
            </Tabs>
        </div>
    );
}
